import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { ArrowRight, GraduationCap, Users } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, user } = useAuth();
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [role, setRole] = useState("student");
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate(user.role === "instructor" ? "/instructor" : "/simulations");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error(t("auth.passwordMin")); return; }
    if (password !== confirmPassword) { toast.error(t("auth.passwordMismatch")); return; }
    setLoading(true);
    try {
      const userData = await register(name, email, password, role, inviteCode);
      toast.success(t("auth.welcomeNew", { name: userData.name }));
      navigate(userData.role === "instructor" ? "/instructor" : "/simulations");
    } catch (error) {
      toast.error(error.response?.data?.detail || t("auth.registrationFailed") || "Error en el registro");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-bg relative overflow-hidden">
      <div className="hero-overlay absolute inset-0" />
      <div className="absolute top-5 right-5 z-20 flex items-center gap-2"><ThemeToggle variant="login" /><LanguageSwitcher variant="login" /></div>
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-[420px] animate-fade-in-up">
          <div className="text-center mb-10">
            <img src="/logo-medsim.png" alt="MedSim" className="h-[185px] mx-auto mb-2" />
          </div>
          <Card className="border border-slate-200/80 shadow-2xl shadow-black/10">
            <CardContent className="p-8">
              <div className="mb-6">
                <h2 className="text-xl font-heading font-medium text-slate-900">{t("auth.register")}</h2>
                <p className="text-sm text-slate-500 mt-1">{t("auth.registerSubtitle")}</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("auth.role")}</Label>
                  <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-3">
                    <label className={`flex flex-col items-center p-3.5 border-2 rounded-lg cursor-pointer transition-all ${role === "student" ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`} data-testid="role-student-option">
                      <RadioGroupItem value="student" className="sr-only" />
                      <GraduationCap className={`w-6 h-6 mb-1.5 ${role === "student" ? "text-slate-900" : "text-slate-400"}`} />
                      <span className={`text-sm font-medium ${role === "student" ? "text-slate-900" : "text-slate-500"}`}>{t("auth.student")}</span>
                    </label>
                    <label className={`flex flex-col items-center p-3.5 border-2 rounded-lg cursor-pointer transition-all ${role === "instructor" ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`} data-testid="role-instructor-option">
                      <RadioGroupItem value="instructor" className="sr-only" />
                      <Users className={`w-6 h-6 mb-1.5 ${role === "instructor" ? "text-slate-900" : "text-slate-400"}`} />
                      <span className={`text-sm font-medium ${role === "instructor" ? "text-slate-900" : "text-slate-500"}`}>{t("auth.instructor")}</span>
                    </label>
                  </RadioGroup>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("auth.inviteCode")}</Label>
                  <Input placeholder="XXXX1234" value={inviteCode} onChange={(e) => setInviteCode(e.target.value.toUpperCase())} required className="h-11 font-mono tracking-widest border-slate-300" data-testid="register-invite-code-input" />
                  <p className="text-xs text-slate-400">{t("auth.inviteCodeHint")}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("auth.name")}</Label>
                  <Input placeholder="María García" value={name} onChange={(e) => setName(e.target.value)} required className="h-11 border-slate-300" data-testid="register-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("auth.email")}</Label>
                  <Input type="email" placeholder="nombre@universidad.edu" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11 border-slate-300" data-testid="register-email-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("auth.password")}</Label>
                    <Input type="password" placeholder="********" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="h-11 border-slate-300" data-testid="register-password-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("auth.confirmPassword")}</Label>
                    <Input type="password" placeholder="********" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} className={`h-11 border-slate-300 ${confirmPassword && password !== confirmPassword ? "border-red-400" : ""}`} data-testid="register-confirm-password-input" />
                  </div>
                </div>
                {confirmPassword && password !== confirmPassword && <p className="text-xs text-red-500">{t("auth.passwordMismatch")}</p>}
                <Button type="submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-md shadow-sm transition-all duration-200" disabled={loading} data-testid="register-submit-btn">
                  {loading ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{t("common.loading")}</span>
                  : <span className="flex items-center gap-2">{t("auth.register")}<ArrowRight className="w-4 h-4" /></span>}
                </Button>
              </form>
              <div className="mt-8 pt-6 border-t border-slate-100 text-center text-sm text-slate-500">
                {t("auth.hasAccount")}{" "}
                <Link to="/login" className="text-slate-900 hover:text-slate-700 font-semibold" data-testid="login-link">{t("auth.login")}</Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
